import { useState, useEffect } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  CircularProgress,
  Chip,
  Link,
  Pagination,
} from '@mui/material';
import { CheckCircle as SuccessIcon, Error as ErrorIcon } from '@mui/icons-material';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface Action {
  id: number;
  postId: string;
  status: string;
  error?: string;
  createdAt: string;
  vkData?: {
    postText: string;
    postUrl: string;
    authorName: string;
    authorPhoto: string;
    date: number;
  } | null;
}

interface ActionsListProps {
  configId: number;
}

export default function ActionsList({ configId }: ActionsListProps) {
  const [actions, setActions] = useState<Action[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 10;

  const loadActions = async (currentPage: number) => {
    setLoading(true);
    try {
      const offset = (currentPage - 1) * limit;
      const res = await api.get(`/auto-liker/${configId}/actions`, {
        params: { limit, offset },
      });
      setActions(res.data.data.actions);
      setTotal(res.data.data.total);
    } catch (err: any) {
      toast.error('Не удалось загрузить историю');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadActions(page);
  }, [configId, page]);

  const handlePageChange = (_: any, value: number) => {
    setPage(value);
  };

  const addNewAction = (action: Action) => {
    setActions((prev) => [action, ...prev].slice(0, limit));
    setTotal((prev) => prev + 1);
  };

  // Expose method for parent to add new actions
  useEffect(() => {
    (window as any)[`addActionToConfig${configId}`] = addNewAction;
    return () => {
      delete (window as any)[`addActionToConfig${configId}`];
    };
  }, [configId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (actions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" align="center" p={3}>
        Действий пока нет
      </Typography>
    );
  }

  return (
    <Box>
      <List>
        {actions.map((action) => (
          <ListItem
            key={action.id}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              mb: 1,
              bgcolor: action.status === 'success' ? 'success.50' : 'error.50',
            }}
          >
            <ListItemAvatar>
              {action.vkData?.authorPhoto ? (
                <Avatar src={action.vkData.authorPhoto} alt={action.vkData.authorName} />
              ) : (
                <Avatar>?</Avatar>
              )}
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  {action.status === 'success' ? (
                    <SuccessIcon color="success" fontSize="small" />
                  ) : (
                    <ErrorIcon color="error" fontSize="small" />
                  )}
                  <Typography variant="body1">
                    {action.vkData?.authorName || 'Unknown Author'}
                  </Typography>
                  <Chip
                    label={action.status}
                    size="small"
                    color={action.status === 'success' ? 'success' : 'error'}
                  />
                </Box>
              }
              secondary={
                <Box>
                  {action.vkData ? (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {action.vkData.postText}
                      </Typography>
                      <Link
                        href={action.vkData.postUrl}
                        target="_blank"
                        rel="noopener"
                        sx={{ fontSize: '0.875rem', mt: 0.5, display: 'inline-block' }}
                      >
                        Открыть пост
                      </Link>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Post ID: {action.postId}
                    </Typography>
                  )}
                  {action.error && (
                    <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                      Error: {action.error}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {new Date(action.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        ))}
      </List>

      {total > limit && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={Math.ceil(total / limit)}
            page={page}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
}
